'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { FaInstagram, FaFacebookF, FaLinkedinIn } from 'react-icons/fa';
import { SiThreads } from 'react-icons/si';

const Footer = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState(2025);

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear());
  }, []);

  const footerLinks = {
    company: [
      { label: 'About Us', href: '/about-hayat-interiors' },
      { label: 'Design Process', href: '/design-process' },
      { label: 'Careers', href: '/about-hayat-interiors#careers' }
    ],
    services: [
      { label: 'Residential Design', href: '/services#residential' },
      { label: 'Modular Solutions', href: '/services#modular' },
      { label: 'Luxury Styling', href: '/services#luxury' },
      { label: 'Renovation', href: '/services#renovation' }
    ],
    resources: [
      { label: 'Project Gallery', href: '/our-projects' },
      { label: 'FAQs', href: '/contact#faq' }
    ],
    contact: [
      { label: 'Book Consultation', href: '/contact' },
      { label: 'Get Quote', href: '/contact#quote' },
      { label: 'Support', href: '/contact#support' },
      { label: 'Locations', href: '/contact#locations' }
    ]
  };

  const socialLinks = [
    { name: 'Instagram', icon: 'CameraIcon', href: 'https://www.instagram.com/hayatinteriors.co/' },
    { name: 'Threads', icon: 'LinkIcon', href: 'https://www.threads.com/@hayatinteriors.co' },
    { name: 'Facebook', icon: 'UserGroupIcon', href: 'https://www.facebook.com/profile.php?id=61584330734970' },
    { name: 'LinkedIn', icon: 'BriefcaseIcon', href: 'https://www.linkedin.com/company/hayat-interiors/?viewAsMember=true' }
  ];

  const iconsMap: Record<string, any> = {
    Instagram: FaInstagram,
    Threads: SiThreads,
    Facebook: FaFacebookF,
    LinkedIn: FaLinkedinIn
  };

  if (!isHydrated) {
    return (
      <footer className="bg-[#1B5E4F] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <div className="text-center">
            <p className="text-sm">Loading...</p>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-zinc-50 border-t border-black/5">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-20 py-12 md:py-16 lg:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-4 sm:col-span-2">
            <Link
              href="/"
              className="inline-block mb-4 md:mb-6"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              <AppImage src="/white-logo.png" alt="Hayat Interiors" width={200} height={28} className="w-44 md:w-[300px]" />
            </Link>
            <p className="text-black/60 mb-6 md:mb-8 max-w-sm leading-relaxed text-sm md:text-base">
              Creating homes that honor both contemporary living and heritage through exceptional craftsmanship.
            </p>
            <div className="flex gap-2 md:gap-3">
              {socialLinks.map((social) => {
                const SocialIcon = iconsMap[social.name];
                return (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 md:w-10 md:h-10 bg-black/5 rounded-full flex items-center justify-center hover:bg-black hover:text-white transition-all duration-300 active:scale-95"
                    aria-label={social.name}
                  >
                    <SocialIcon size={15} className="md:w-4 md:h-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Company Links */}
          <div className="lg:col-span-2">
            <h3 className="text-xs md:text-sm font-medium text-black mb-4 md:mb-6 uppercase tracking-wider">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-black/60 hover:text-black transition-colors duration-200 text-sm font-normal"
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div className="lg:col-span-2">
            <h3 className="text-sm font-medium text-black mb-6 uppercase tracking-wider">Services</h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-black/60 hover:text-black transition-colors duration-200 text-sm"
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div className="lg:col-span-2">
            <h3 className="text-sm font-medium text-black mb-6 uppercase tracking-wider">Resources</h3>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-black/60 hover:text-black transition-colors duration-200 text-sm"
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Links */}
          <div className="lg:col-span-2">
            <h3 className="text-sm font-medium text-black mb-6 uppercase tracking-wider">Contact</h3>
            <ul className="space-y-3">
              {footerLinks.contact.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-black/60 hover:text-black transition-colors duration-200 text-sm "
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 md:pt-12 border-t border-black/5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-6">
            <p className="text-xs md:text-sm text-black/50 font-normal text-center md:text-left">
              © {currentYear} Hayat Interiors. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8">
              <Link
                href="/privacy-policy"
                className="text-sm text-black/50 hover:text-black transition-colors duration-200 font-normal"
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              >
                Privacy Policy
              </Link>
             
              <Link
                href="http://talaganarajesh.in/"
                className="text-sm text-black/50 hover:text-black transition-colors duration-200 font-normal"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              >
                Developed by webcros
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;